import { Fallback } from '@cfra-nextgen-frontend/shared';
import { ErrorBoundary } from '@cfra-nextgen-frontend/shared/src/components/Fallbacks/ErrorBoundary';
import { ProjectSpecificResourcesContextProvider } from '@cfra-nextgen-frontend/shared/src/components/ProjectSpecificResourcesContext/Context';
import { getAwsAmplifyLocalStorageKey } from '@cfra-nextgen-frontend/shared/src/utils/auth';
import ScrollToTop, { ScrollingContextProvider } from '@cfra-nextgen-frontend/shared/src/utils/scrolling';
import { Hub } from 'aws-amplify';
import React from 'react';
import { Navigate, Outlet, Route } from 'react-router-dom';
import { GetAuthenticatedRoutesJsxProps, RouteType, SetUpAwsAmplifyRememberMeListener } from './types';
import { UserInitialPreferencesContextProvider } from '../components/UserPreferences/UserInitialPreferencesContext';
import { PreLoginRouteHandler } from './PreLoginRouteHandler';

export function getNotAuthenticatedRoutes(
    LoginElement: JSX.Element,
    unauthenticatedRoutes: Array<RouteType> = [],
): Array<RouteType<React.FC>> {
    return [
        ...unauthenticatedRoutes,
        { path: '/login', element: LoginElement },
        {
            path: '*',
            element: <Navigate to={`/login${window.location.search?.includes('token=') ? `?token=${new URLSearchParams(window.location.search).get('token')}` : ''}`} />,
        },
    ];
}

export function getNotAuthenticatedRoutesJsx(routes: Array<RouteType>) {
    return (
        <Route
            element={
                <ErrorBoundary FallbackComponent={Fallback.ErrorFallback}>
                    <Outlet />
                </ErrorBoundary>
            }
            children={routes.map((route) => {
                const { path, element, Wrapper, wrapperProps } = route;
                return (
                    <Route
                        key={path}
                        path={path}
                        element={Wrapper ? <Wrapper {...wrapperProps}>{element}</Wrapper> : element}
                    />
                );
            })}
        />
    );
}

export function getAuthenticatedRoutesJsx({
    authenticatedRoutesWrapperJsx,
    projectSpecificResources,
    routes,
    userContextProviderProps,
}: GetAuthenticatedRoutesJsxProps) {
    return (
        <Route
            element={
                <ErrorBoundary FallbackComponent={Fallback.ErrorFallback}>
                    <PreLoginRouteHandler>
                        <ScrollingContextProvider>
                            <ScrollToTop />
                            <ProjectSpecificResourcesContextProvider
                                projectSpecificResources={projectSpecificResources || {}}>
                                {userContextProviderProps ? (
                                    <UserInitialPreferencesContextProvider {...userContextProviderProps}>
                                        {authenticatedRoutesWrapperJsx}
                                    </UserInitialPreferencesContextProvider>
                                ) : (
                                    <>{authenticatedRoutesWrapperJsx}</>
                                )}
                            </ProjectSpecificResourcesContextProvider>
                        </ScrollingContextProvider>
                    </PreLoginRouteHandler>
                </ErrorBoundary>
            }
            children={routes.map((route) => {
                const { path, element, Wrapper, wrapperProps } = route;
                return (
                    <Route
                        key={path}
                        path={path}
                        element={Wrapper ? <Wrapper {...wrapperProps}>{element}</Wrapper> : element}
                    />
                );
            })}
        />
    );
}

export const setUpAwsAmplifyRememberMeListener: SetUpAwsAmplifyRememberMeListener = (rememberMe) => {
    const awsAmplifyListener = (data: any) => {
        switch (data?.payload?.event) {
            case 'signIn':
                if (rememberMe.current) {
                    return;
                }

                const refreshTokenKey = getAwsAmplifyLocalStorageKey({
                    username: data?.payload?.data?.username,
                    key: 'refreshToken',
                });

                localStorage.removeItem(refreshTokenKey);
                break;
            case 'signOut':
                rememberMe.current = false;
                break;
        }
    };

    Hub.listen('auth', awsAmplifyListener);
};
